/* Listing media: the photo gallery and the video link.
 *
 * FEATURE DETECTION. `photo_urls` and `video_url` arrived with DB migration
 * 002 and supabase-proxy v3.8.3. Everything here treats them as possibly
 * absent, so the client behaves correctly against an older proxy and against
 * every listing created before the columns existed:
 *
 *   - a listing with no `photo_urls` falls back to `[photo_url]`, so a
 *     single-photo listing renders exactly as it always did
 *   - a listing with no `video_url` renders no player and no badge
 *
 * COVER IS FIRST. `photo_urls[0]` is the cover and mirrors `photo_url`, which
 * is kept precisely so cards, search results and saved snapshots need no
 * change. Reordering the gallery rewrites both.
 */

/** The gallery for a listing, oldest contract to newest, never duplicated. */
export function galleryOf(
  photoUrls: string[] | null | undefined,
  photoUrl: string | null | undefined,
): string[] {
  const list = Array.isArray(photoUrls)
    ? photoUrls.filter((u): u is string => typeof u === "string" && !!u.trim())
    : [];
  // Never truncated here: the cap on how many a lister may ADD is the market's
  // media.max_photos row (src/lib/listingLimits.ts); a row that already holds
  // more is shown whole, or the gallery would lie about what is stored.
  if (list.length) return list;
  return photoUrl ? [photoUrl] : [];
}

/* ── YouTube ───────────────────────────────────────────────────────────────
 * Only YouTube is accepted, because only YouTube was ordered. An unrecognised
 * URL is rejected with a message rather than stored and quietly ignored --
 * a lister who pastes a Vimeo link deserves to be told, not to wonder why no
 * player appeared.
 */

const YT_HOSTS = ["youtube.com", "www.youtube.com", "m.youtube.com", "youtu.be", "www.youtu.be"];

/** The 11-character video id, or null if this is not a YouTube URL we know. */
export function youTubeId(raw: string | null | undefined): string | null {
  const text = (raw ?? "").trim();
  if (!text) return null;
  let url: URL;
  try {
    url = new URL(text.startsWith("http") ? text : `https://${text}`);
  } catch {
    return null;
  }
  if (!YT_HOSTS.includes(url.hostname.toLowerCase())) return null;

  // youtu.be/<id>
  if (url.hostname.toLowerCase().endsWith("youtu.be")) {
    const id = url.pathname.replace(/^\//, "").split("/")[0];
    return /^[\w-]{11}$/.test(id) ? id : null;
  }
  // youtube.com/watch?v=<id>
  const v = url.searchParams.get("v");
  if (v && /^[\w-]{11}$/.test(v)) return v;
  // youtube.com/embed/<id>, /shorts/<id>, /live/<id>
  const m = /^\/(embed|shorts|live|v)\/([\w-]{11})/.exec(url.pathname);
  return m ? m[2] : null;
}

/** The canonical watch URL we store, so the DB never holds five spellings of
 *  one video. Returns null when the input is not a YouTube link. */
export function canonicalYouTube(raw: string | null | undefined): string | null {
  const id = youTubeId(raw);
  return id ? `https://www.youtube.com/watch?v=${id}` : null;
}

/** Privacy-enhanced embed: youtube-nocookie.com sets no tracking cookie until
 *  the visitor actually plays. The site loads zero Google anything otherwise,
 *  and this is the closest an embedded player gets to that. */
export function youTubeEmbed(raw: string | null | undefined): string | null {
  const id = youTubeId(raw);
  return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0` : null;
}

/** The still the player shows before it loads. Served by YouTube, requested
 *  only when the visitor has scrolled the block into view. */
export function youTubeThumb(raw: string | null | undefined): string | null {
  const id = youTubeId(raw);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;
}
